import { useEffect } from 'react';

function isInteracting(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  if (el.closest('[role="menu"]')) return true;
  return false;
}

interface UseKeyboardShortcutsOptions {
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  clearSelection: () => void;
  openDispatch: () => void;
  toggleShortcutOverlay: () => void;
  undo?: () => void;
  redo?: () => void;
  canEdit: boolean;
  canDispatch: boolean;
  isDialogOpen: boolean;
}

export function useKeyboardShortcuts({
  searchInputRef,
  clearSelection,
  openDispatch,
  toggleShortcutOverlay,
  undo,
  redo,
  canEdit,
  canDispatch,
  isDialogOpen,
}: UseKeyboardShortcutsOptions) {
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const modifier = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();

      if (canEdit && modifier && !e.altKey && !isDialogOpen && !isInteracting(e)) {
        if (key === 'z') {
          e.preventDefault();
          if (e.shiftKey) {
            redo?.();
          } else {
            undo?.();
          }
          return;
        }

        if (key === 'y' && !e.shiftKey) {
          e.preventDefault();
          redo?.();
          return;
        }
      }

      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'Escape') {
        if (!isDialogOpen && !isInteracting(e)) clearSelection();
        return;
      }

      if (isInteracting(e)) return;
      if (isDialogOpen && e.key !== '?') return;

      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      if (e.key === 'd' || e.key === 'D') {
        if (canDispatch) {
          openDispatch();
        }
        return;
      }

      if (e.key === '?') {
        e.preventDefault();
        toggleShortcutOverlay();
        return;
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [searchInputRef, clearSelection, openDispatch, toggleShortcutOverlay, undo, redo, canEdit, canDispatch, isDialogOpen]);
}
